import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { BoltIcon } from '@heroicons/react/24/outline';
import { ConnectInstantPayoutsPromotion } from '@stripe/react-connect-js';
import { PageHeader } from '../../../components/ui';
import { trackGleapEvent } from '../../../utils/gleapEvents';
import connectApi from '../../../services/connectApi';
import LegacyFinance from '../finance';
import FirstPayoutBanner from '../../connect/finance/first-payout-banner';
import ConnectBalanceHero from '../../connect/finance/connect-balance-hero';
import PayoutActivityTable from '../../connect/finance/payout-activity-table';
import RecentOrdersTable from '../../connect/finance/recent-orders-table';
import PayoutHealthLine from '../../connect/finance/payout-health-line';
import ConnectPayoutAccount from '../../connect/finance/connect-payout-account';
import PayoutScheduleModal from '../../connect/finance/payout-schedule-modal';
import COPY from '../../connect/finance/connect-finance-copy';
import { useFinanceSection } from '../section/finance-section-context';
import { upcomingPayouts } from '../../connect/finance/upcoming-payout';
import FinancePageShell from './finance-page-shell';
import AdminFinance from '../admin/admin-finance';
import ADMIN_BODIES from '../admin/admin-bodies';

// How many rows each preview shows. Five payouts and six orders — the mock
// draws them so and says the two cards are deliberately not row-matched.
const PAYOUT_PREVIEW = 5;
const ORDER_PREVIEW = 6;

// How far back each preview looks. Different windows because these are
// different cadences: payouts arrive weekly, so three months is a dozen
// rows, while orders arrive all day and an unbounded read would pull
// thousands to show six. Each empty state names its own window.
const PAYOUT_DAYS = 90;
const ORDER_DAYS = 30;

// How long the page keeps asking for a payout Stripe has just made. Stripe's
// card answers the moment the payout exists; our list learns of it from the
// payout.created webhook, a second or a few later, so a single re-read came
// back without the row and the list stayed stale until a refresh (owner,
// 2026-10-06). One read at once, then these waits: six reads over 19 seconds.
// After that the row arrives with the next visit.
const INSTANT_PAYOUT_REREAD_MS = [1000, 2000, 3000, 5000, 8000];

const SECTION_COPY = { balance: 'balance', payouts: 'payoutActivity', orders: 'recentOrders' };

const since = (days) => moment.utc().subtract(days, 'days').toISOString();
const until = () => moment.utc().toISOString();
// The payout list's own upper bound is a day ahead of this browser's clock.
// The API keeps rows created up to the bound, by the server's clock: a
// browser a few seconds slow asked for a window that ended before the payout
// it had just made, and no number of re-reads could find it.
const untilTomorrow = () => moment.utc().add(1, 'day').toISOString();

const READERS = {
  balance: async (companyId) => {
    const balance = connectApi.toConnectBalance(await connectApi.getConnectBalance(companyId));
    if (!balance) throw new Error('balance');
    return balance;
  },
  payouts: async (companyId) =>
    connectApi.toFinancePayouts(await connectApi.getFinancePayouts(companyId, since(PAYOUT_DAYS), untilTomorrow())),
  orders: async (companyId) => {
    const data = connectApi.toOrderReports(
      await connectApi.getOrderReports(companyId, since(ORDER_DAYS), until(), undefined, 1, ORDER_PREVIEW),
    );
    if (!data) throw new Error('orders');
    return data.rows;
  },
};

// Newest first, whatever order the API returned them in: the preview's
// whole claim is "the most recent", and sorting here makes it true.
const newestFirst = (rows, dateOf) =>
  [...rows].sort((a, b) => new Date(dateOf(b) || 0) - new Date(dateOf(a) || 0));

// The mock's list card: a padded card, a head with the title and a link and
// no rule under it; the table below draws its own column-header row.
const ListCard = ({ title, link, linkLabel, children }) => (
  <section className="rounded-xl border border-fin-border bg-fin-surface p-[22px_26px]">
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-[15px] font-bold tracking-[-0.01em] text-fin-ink">{title}</h2>
      {link ? (
        <Link to={link} className="text-[13px] font-semibold text-fin-blue hover:text-fin-blue-hover">
          {linkLabel}
        </Link>
      ) : null}
    </div>
    {children}
  </section>
);

// Owner ruling 2026-10-12 (the md over the design's dev note): when Stripe
// will not draw its card, or there is no session to draw it with, the row
// says so plainly — no amount, no button.
const InstantPayoutsUnavailable = () => (
  <section
    data-testid="instant-payouts-unavailable"
    className="flex items-center gap-3.5 rounded-xl border border-fin-border bg-fin-surface px-[22px] py-4"
  >
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-fin-border bg-fin-recess">
      <BoltIcon className="h-4 w-4 stroke-2 text-fin-faintest" aria-hidden="true" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-bold text-fin-ink">Instant Payouts aren&apos;t available right now</p>
      <p className="mt-0.5 text-[13px] text-fin-muted">Your balance will be paid out on your regular schedule.</p>
    </div>
  </section>
);

// Where the money stands right now, and the short way to each place that
// explains it. Stripe's own instant-payouts card sits under the balance card
// (Overview v3, frame 1a), full width; "View all payouts" goes to Payouts and
// the strip's "Manage" to Business Details' Payout details card (owner,
// 2026-09-23). "Edit" beside the rail's Schedule row opens the restaurant's
// own schedule modal (owner, 2026-10-20: restaurants edit their payout
// schedule again; the old "Payout settings" button stays gone). None of
// these is drawn for a viewer the Payouts route refuses.
const Overview = () => {
  const { model, isAdmin, companyId, status, canManagePayouts, payoutsPaused, instance, sessionFailure } =
    useFinanceSection();
  const navigate = useNavigate();
  const [sections, setSections] = useState({
    balance: { data: null, error: '', loading: true },
    payouts: { data: [], error: '', loading: true },
    orders: { data: [], error: '', loading: true },
  });
  const requestId = useRef(0);
  // The balance's own read generation, beside requestId's company one. A
  // saved schedule moves it on, so a balance read already in flight when
  // the save lands (Retry's, or the re-read after an instant payout) cannot
  // put the schedule from before the save back on the card. requestId is
  // not bumped for that: it would end followInstantPayout's loop too.
  const balanceRead = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Owner, 2026-10-20: the restaurant edits its own schedule from Edit
  // beside the hero's Schedule row. The modal answers with the balance the
  // API re-read after the save, so the card updates without a second round
  // trip; a null answer means the save stood but that re-read failed, and
  // the page reads it itself. The payout list is not re-read (a schedule
  // change creates no payout); the Upcoming rows follow
  // sections.balance.data by themselves.
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  // The read generation the modal was opened in: a save that lands after
  // the company changed (requestId moved on) or after unmount is dropped,
  // as load() and followInstantPayout drop theirs — the old company's
  // schedule must not land on the new company's card.
  const scheduleRequest = useRef(0);
  const openScheduleModal = () => {
    scheduleRequest.current = requestId.current;
    setScheduleModalOpen(true);
  };

  const copy = COPY.restaurant;
  const ready = !!status?.hasAccount && status?.connectStatus === 'READY';

  const load = useCallback(
    async (key, id) => {
      setSections((prev) => ({ ...prev, [key]: { ...prev[key], loading: true, error: '' } }));
      const read = key === 'balance' ? ++balanceRead.current : null;
      const stale = () =>
        !mounted.current || id !== requestId.current || (read !== null && read !== balanceRead.current);
      try {
        const data = await READERS[key](companyId);
        if (stale()) return;
        setSections((prev) => ({ ...prev, [key]: { data, error: '', loading: false } }));
      } catch {
        if (stale()) return;
        // Never an empty list over a refused read.
        setSections((prev) => ({
          ...prev,
          [key]: { ...prev[key], error: copy.sections[SECTION_COPY[key]].error, loading: false },
        }));
      }
    },
    [companyId, copy],
  );

  const scheduleSaved = (next) => {
    const id = scheduleRequest.current;
    if (!mounted.current || id !== requestId.current) return;
    if (next) {
      // Any balance read still in flight predates the save.
      balanceRead.current += 1;
      setSections((prev) => ({ ...prev, balance: { data: next, error: '', loading: false } }));
    } else load('balance', id);
  };

  // The reads that follow an instant payout, until Stripe's own id for it is
  // on the list. Quiet on purpose: they are the page's business, so they
  // never raise the skeleton or the error line over the rows on screen, and a
  // refused one just waits for the next. `id` is the same stale guard `load`
  // uses — another company, or a page that is gone, ends the loop.
  const followInstantPayout = useCallback(
    async (payoutId, id) => {
      for (const wait of [0, ...INSTANT_PAYOUT_REREAD_MS]) {
        if (wait) await new Promise((resolve) => { setTimeout(resolve, wait); });
        if (!mounted.current || id !== requestId.current) return;
        try {
          const data = await READERS.payouts(companyId);
          if (!mounted.current || id !== requestId.current) return;
          setSections((prev) => ({ ...prev, payouts: { data, error: '', loading: false } }));
          if (data.some((row) => row.stripePayoutId === payoutId)) return;
        } catch {
          // The list on screen stands.
        }
      }
    },
    [companyId],
  );

  useEffect(() => {
    // Whatever changed, anything asked for before it is stale: the bump
    // comes first, so a company that stops being on direct charges ends the
    // reads in flight as surely as a company switch does.
    requestId.current += 1;
    // The schedule modal was opened for what is now stale; it closes.
    setScheduleModalOpen(false);
    if (model !== 'CONNECT_DIRECT' || !companyId) return;
    const id = requestId.current;
    if (ready) {
      load('balance', id);
      load('payouts', id);
      load('orders', id);
    }
  }, [model, companyId, ready, load]);

  // The Upcoming rows (spec 2026-09-22 §F) take as many of the preview's
  // five visible slots as there are expected arrival dates, so the real
  // rows below them slice down accordingly — never below two, so a company
  // with several arrival dates still sees at least a couple of real payouts.
  const upcoming = useMemo(() => upcomingPayouts(sections.balance.data), [sections.balance.data]);
  // The card waits for the balance too (owner, 2026-09-24): the list lands
  // first, and the Upcoming rows arriving a second later shifted which
  // payouts fit the preview. First read only — a balance on file or a
  // refused read ends the wait, and the list then shows without Upcoming.
  const awaitingUpcoming =
    sections.balance.loading && !sections.balance.data && !sections.balance.error;
  const payoutRows = useMemo(
    () =>
      newestFirst(sections.payouts.data || [], (r) => r.createdAt).slice(
        0,
        Math.max(PAYOUT_PREVIEW - upcoming.length, 2),
      ),
    [sections.payouts.data, upcoming],
  );
  const orderRows = useMemo(
    () => newestFirst(sections.orders.data || [], (r) => r.createdAt).slice(0, ORDER_PREVIEW),
    [sections.orders.data],
  );

  // The strip's Manage is a bank change, and the bank is changed in
  // Stripe's own account form on Business Details' Payout details card
  // (owner, 2026-09-23) — the page that already manages the rest of the
  // account, so there is one place for it rather than a modal of its own.
  // Business Details reads the account fresh on arrival and scrolls to the
  // card.
  const goToPayoutDetails = () => navigate('/dashboard/finance/business-details#payout-details');

  // Stripe tells us, once it has looked, whether it will draw the card at
  // all. Until it says so the card takes no room, rather than an empty
  // box under the balance card. The waiting card is parked OFF SCREEN
  // rather than in a 1px clipped box: while it loads Stripe draws its iframe
  // at 328×128, and a cross-origin iframe receives real mouse input through
  // an ancestor's overflow clip — sitting on top of a button that then
  // shared its row, it swallowed every click in the page's first second
  // (reproduced on fe-test 2026-09-22; elementFromPoint still said "button",
  // only trusted clicks were lost).
  // Null until Stripe answers.
  const [promotionShown, setPromotionShown] = useState(null);

  // Stripe's own instant-payouts card (spec 2026-09-22 §4, rail correction):
  // it draws nothing when this account has no instant-eligible funds, and it
  // alone decides eligibility and fees, because it carries the losses on
  // that decision. Re-reads both the balance and the payout list on a
  // successful instant payout — the balance drops and the list gains a row,
  // the row a little later than the balance (followInstantPayout).
  // Session-gated here (not inside the hero) because only this page knows
  // what "changed" means for it; a failed session draws no card, and the
  // row says plainly that instant payouts are not available
  // (InstantPayoutsUnavailable).
  const instantPayoutPromotion = sessionFailure || !instance ? null : (
    <div
      data-testid="instant-payouts-row"
      className={promotionShown ? 'w-full' : 'fixed -left-[10000px] top-0 w-[360px]'}
      aria-hidden={promotionShown ? undefined : true}
    >
      <ConnectInstantPayoutsPromotion
        onInstantPayoutsPromotionLoaded={({ promotionShown: shown }) => setPromotionShown(!!shown)}
        onInstantPayoutCreated={({ payoutId } = {}) => {
          trackGleapEvent('instant_payout_created', { payoutId: payoutId || null });
          load('balance', requestId.current);
          if (payoutId) followInstantPayout(payoutId, requestId.current);
          else load('payouts', requestId.current);
        }}
      />
    </div>
  );

  // Review focus 5: only an admin sees the console. Restaurant roles reach
  // this branch only on the legacy model, which LegacyFinance still draws.
  if (model !== 'CONNECT_DIRECT') return isAdmin ? <AdminFinance bodies={ADMIN_BODIES} /> : <LegacyFinance />;

  return (
    <FinancePageShell>
      {/* The health line is a fact about the account, not an action, so it
          sits beside the description rather than in PageHeader's actions
          slot where round 1 put it — `items-end` + `pb-[2px]` lands it on
          the description's baseline (spec 2026-09-22 §4). */}
      <div className="flex items-end justify-between gap-4">
        <PageHeader
          title="Overview"
          description="Your balance, upcoming payouts, and recent activity."
          className="flex-1"
        />
        {ready ? (
          <div className="shrink-0 pb-[2px]">
            <PayoutHealthLine
              status={status}
              payouts={sections.payouts.data}
              loading={sections.payouts.loading}
              paused={payoutsPaused}
            />
          </div>
        ) : null}
      </div>

      {ready ? (
        <>
          <FirstPayoutBanner />
          <ConnectBalanceHero
            balance={sections.balance.data}
            loading={sections.balance.loading}
            error={sections.balance.error}
            onRetry={() => load('balance', requestId.current)}
            canManagePayouts={canManagePayouts}
            onManage={() => navigate('/dashboard/finance/payouts')}
            onEditSchedule={openScheduleModal}
          />
          {canManagePayouts ? (
            <PayoutScheduleModal
              open={scheduleModalOpen}
              balance={sections.balance.data}
              companyId={companyId}
              onClose={() => setScheduleModalOpen(false)}
              onSaved={scheduleSaved}
            />
          ) : null}

          {canManagePayouts ? (
            <>
              {instantPayoutPromotion}
              {promotionShown === false || sessionFailure ? <InstantPayoutsUnavailable /> : null}
            </>
          ) : null}

          <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2">
            <ListCard
              title="Payout activity"
              link={canManagePayouts ? '/dashboard/finance/payouts' : null}
              linkLabel="All payouts"
            >
              <PayoutActivityTable
                variant="compact"
                ariaLabel="Payout activity"
                rows={awaitingUpcoming ? [] : payoutRows}
                upcoming={upcoming}
                loading={sections.payouts.loading || awaitingUpcoming}
                error={sections.payouts.error}
                onRetry={() => load('payouts', requestId.current)}
                empty={`No payouts in the last ${PAYOUT_DAYS} days.`}
                onOpen={
                  canManagePayouts
                    ? (stripePayoutId) => navigate(`/dashboard/finance/payouts/${stripePayoutId}`)
                    : undefined
                }
              />
            </ListCard>
            <ListCard title="Recent orders" link="/dashboard/finance/order-reports" linkLabel="View all">
              <RecentOrdersTable
                rows={orderRows}
                loading={sections.orders.loading}
                error={sections.orders.error}
                onRetry={() => load('orders', requestId.current)}
                empty={`No paid orders in the last ${ORDER_DAYS} days.`}
              />
            </ListCard>
          </div>

          <ConnectPayoutAccount
            balance={sections.balance.data}
            canManagePayouts={canManagePayouts}
            onManage={goToPayoutDetails}
          />
        </>
      ) : (
        <p className="text-sm text-fin-ink-2">
          {copy.accountNotReady.before}
          <Link to="/dashboard/finance/business-details" className="font-medium text-fin-blue hover:underline">
            {copy.accountNotReady.linkLabel}
          </Link>
          {copy.accountNotReady.after}
        </p>
      )}
    </FinancePageShell>
  );
};

export default Overview;
